import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Heart, Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const DonatePage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [donationType, setDonationType] = useState('one-time');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);

  const presetAmounts = [25, 50, 100, 250, 500, 1000];

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      toast.error('Payment status check timed out. Please contact us if you completed payment.');
      setIsCheckingPayment(false);
      return;
    }

    setIsCheckingPayment(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/donate/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        toast.success('Thank you for your generous donation! Your payment has been processed successfully.');
        setIsCheckingPayment(false);
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setIsCheckingPayment(false);
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), pollInterval);
    }
  };

  const handleDonate = async () => {
    const selectedAmount = customAmount || amount;
    
    if (!selectedAmount || parseFloat(selectedAmount) <= 0) {
      toast.error('Please select or enter a valid donation amount');
      return;
    }

    setIsProcessing(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const originUrl = window.location.origin;
      
      const response = await fetch(`${BACKEND_URL}/api/donate/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: parseFloat(selectedAmount),
          donation_type: donationType,
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast.error('An error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const features = [
    'Safe housing for expecting mothers',
    'Prenatal care and support services',
    'Family stabilization programs',
    'Community restoration initiatives',
    'Generational impact and legacy building',
    'Faith-based guidance and counseling'
  ];

  if (isCheckingPayment) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-gold animate-spin mx-auto mb-6" />
          <p className="text-xl text-emerald-900">Processing your donation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/pnrq309j_E259CC80-8178-4D03-BDB6-C1A9EF07CE7C.png"
            alt="Support Our Mission"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-block mb-6">
            <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
            <h1 className="font-serif text-5xl md:text-7xl text-emerald-900 mb-6">Build Legacy With Us</h1>
            <div className="h-px w-16 bg-gold mx-auto"></div>
          </div>
          <p className="text-xl md:text-2xl text-navy leading-relaxed">
            Your generosity creates lasting change for mothers, families, and generations to come
          </p>
        </div>
      </section>

      {/* Donation Form Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Donation Form */}
            <div className="bg-ivory p-8 md:p-12 rounded-sm shadow-xl">
              <h2 className="font-serif text-3xl text-emerald-900 mb-8">Make Your Gift</h2>

              {/* Donation Type */}
              <div className="mb-8">
                <Label className="text-emerald-900 font-semibold mb-4 block">Donation Type</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setDonationType('one-time')}
                    className={`p-4 rounded-sm border-2 transition-all duration-300 ${
                      donationType === 'one-time'
                        ? 'border-gold bg-gold/10 text-emerald-900 font-semibold'
                        : 'border-emerald-900/20 text-navy hover:border-gold'
                    }`}
                  >
                    One-Time Gift
                  </button>
                  <button
                    onClick={() => setDonationType('monthly')}
                    className={`p-4 rounded-sm border-2 transition-all duration-300 ${
                      donationType === 'monthly'
                        ? 'border-gold bg-gold/10 text-emerald-900 font-semibold'
                        : 'border-emerald-900/20 text-navy hover:border-gold'
                    }`}
                  >
                    Monthly Gift
                  </button>
                </div>
              </div>

              {/* Preset Amounts */}
              <div className="mb-8">
                <Label className="text-emerald-900 font-semibold mb-4 block">Select Amount</Label>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {presetAmounts.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => {
                        setAmount(preset.toString());
                        setCustomAmount('');
                      }}
                      className={`p-4 rounded-sm border-2 transition-all duration-300 ${
                        amount === preset.toString() && !customAmount
                          ? 'border-gold bg-gold text-emerald-900 font-bold'
                          : 'border-emerald-900/20 text-navy hover:border-gold'
                      }`}
                    >
                      ${preset}
                    </button>
                  ))}
                </div>
                
                {/* Custom Amount */}
                <div>
                  <Label htmlFor="customAmount" className="text-emerald-900 mb-2 block">
                    Or enter custom amount
                  </Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-900 font-semibold text-lg">
                      $
                    </span>
                    <input
                      id="customAmount"
                      type="number"
                      min="1"
                      step="1"
                      value={customAmount}
                      onChange={(e) => {
                        setCustomAmount(e.target.value);
                        setAmount('');
                      }}
                      className="w-full pl-8 pr-4 py-3 border-2 border-emerald-900/20 rounded-sm focus:border-gold focus:ring-gold focus:outline-none text-lg"
                      placeholder="Enter amount"
                    />
                  </div>
                </div>
              </div>

              {/* Donate Button */}
              <Button
                onClick={handleDonate}
                disabled={isProcessing}
                className="w-full bg-emerald-900 hover:bg-emerald-800 text-white text-lg py-6 rounded-sm font-semibold transition-all duration-300 hover:scale-[1.02]"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Complete Donation
                    <ArrowRight className="ml-3 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-sm text-navy/60 text-center mt-6">
                Secure payment powered by Stripe. All donations are tax-deductible.
              </p>
            </div>

            {/* Impact Information */}
            <div>
              <div className="mb-8">
                <h3 className="font-serif text-3xl text-emerald-900 mb-6">Your Impact</h3>
                <p className="text-lg text-navy leading-relaxed mb-6">
                  Every dollar you give creates lasting change. Your donation doesn't just provide 
                  immediate support—it builds legacy, restores families, and creates opportunities 
                  that span generations.
                </p>
              </div>

              <div className="bg-white p-8 rounded-sm shadow-lg mb-8">
                <h4 className="font-serif text-xl text-emerald-900 mb-6">Your Donation Supports:</h4>
                <ul className="space-y-4">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center">
                          <Check className="h-4 w-4 text-gold" />
                        </div>
                      </div>
                      <span className="ml-4 text-navy">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-emerald-900 text-white p-8 rounded-sm">
                <Heart className="h-12 w-12 text-gold mb-4" />
                <h4 className="font-serif text-2xl text-gold mb-4">Monthly Giving</h4>
                <p className="text-ivory/90 leading-relaxed mb-4">
                  Join our Legacy Circle with a monthly gift. Consistent support allows us to plan 
                  for the future, expand our programs, and create sustainable impact.
                </p>
                <p className="text-ivory/80 text-sm italic">
                  Monthly donors receive exclusive updates and invitations to foundation events.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-20 px-6 bg-ivory">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block mb-12">
            <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
            <h2 className="font-serif text-4xl text-emerald-900 mb-4">Donors Like You Make This Possible</h2>
            <div className="h-px w-16 bg-gold mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-sm shadow-md">
              <div className="text-5xl font-serif text-gold mb-3">$50</div>
              <div className="text-lg font-semibold text-emerald-900 mb-2">Provides</div>
              <div className="text-navy">One week of prenatal care and nutritional support for an expecting mother</div>
            </div>
            <div className="bg-white p-8 rounded-sm shadow-md">
              <div className="text-5xl font-serif text-gold mb-3">$250</div>
              <div className="text-lg font-semibold text-emerald-900 mb-2">Provides</div>
              <div className="text-navy">One month of safe housing, counseling, and family stabilization services</div>
            </div>
            <div className="bg-white p-8 rounded-sm shadow-md">
              <div className="text-5xl font-serif text-gold mb-3">$1000</div>
              <div className="text-lg font-semibold text-emerald-900 mb-2">Provides</div>
              <div className="text-navy">Comprehensive support package for a family's complete restoration journey</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DonatePage;
